//
// Copyright (c) 2011-2026 by Curalate, Inc.
//

import React, { useCallback, useMemo } from 'react';
import styled, { css } from 'styled-components';

import HR from '@components/atoms/hr/HR';
import { CollapsibleSection } from '@components/media-filters/CollapsibleSection';
import {
  FilterSection,
  FilterSectionTitle,
  FilterValue,
} from '@components/media-filters/filterSectionModels';
import {
  DEFAULT_VIDEO_DURATION_FILTER_SECTION,
  MAX_VIDEO_DURATION_INDEX,
  OPEN_ENDED_THRESHOLD_SECONDS,
  VIDEO_DURATION_BREAKPOINTS,
  VIDEO_DURATION_PRESETS,
  VIDEO_DURATION_TICK_LABELS,
  VideoDurationPreset,
} from '@components/media-filters/videoDurationFilterConstants';
import { parseVideoDurationRange } from '@components/media-filters/videoDurationFilterUtils';
import { Slider } from '@components/primitives/slider/Slider';
import {
  MEDIA_LIBRARY_FILTER_PANEL_TRACKING_ID_PREFIX,
  MediaLibraryTrackingIds,
} from '@media-library/constants/mediaLibraryTrackingIds';
import { withTracking } from '@utils/withTracking';

export const formatVideoDurationLabel = (min: number, max: number): string => {
  if (max >= OPEN_ENDED_THRESHOLD_SECONDS) {
    return `≥ ${formatDurationValue(min)}`;
  }

  return `${formatDurationValue(min)} – ${formatDurationValue(max)}`;
};

const formatDurationValue = (seconds: number): string => {
  if (seconds >= 60) {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return remainingSeconds > 0
      ? `${minutes}m ${remainingSeconds}s`
      : `${minutes}m`;
  }
  return `${seconds}s`;
};

const indexToSeconds = (index: number): number =>
  VIDEO_DURATION_BREAKPOINTS[index] ?? 0;

const secondsToIndex = (seconds: number): number => {
  let closestIndex = 0;
  let closestDistance = Infinity;

  VIDEO_DURATION_BREAKPOINTS.forEach((breakpoint, index) => {
    const distance = Math.abs(breakpoint - seconds);
    if (distance < closestDistance) {
      closestDistance = distance;
      closestIndex = index;
    }
  });

  return closestIndex;
};

interface VideoDurationFilterProps {
  isInitiallyCollapsed?: boolean;
  onChange: (section: FilterSection<FilterValue>) => void;
  section: FilterSection<string> | undefined;
}

export const VideoDurationFilter = ({
  isInitiallyCollapsed,
  onChange,
  section = DEFAULT_VIDEO_DURATION_FILTER_SECTION,
}: VideoDurationFilterProps) => {
  const { maxIndex, minIndex } = useMemo(() => {
    const selectedOption = section.options.find((opt) => opt.isSelected);

    if (selectedOption) {
      const { max = OPEN_ENDED_THRESHOLD_SECONDS, min } =
        parseVideoDurationRange(selectedOption.value);
      return { minIndex: secondsToIndex(min), maxIndex: secondsToIndex(max) };
    }

    return { minIndex: 0, maxIndex: 0 };
  }, [section.options]);

  const isActive = section.options.some((opt) => opt.isSelected);

  const emitChange = useCallback(
    (min: number, max: number) => {
      onChange({
        paramKey: 'videoDuration',
        options: [
          {
            isSelected: true,
            label: formatVideoDurationLabel(min, max),
            value: `${min}-${max}`,
          },
        ],
      });
    },
    [onChange],
  );

  const handleSliderChange = (value: number | number[]) => {
    if (Array.isArray(value) && value[0] != null && value[1] != null) {
      emitChange(indexToSeconds(value[0]), indexToSeconds(value[1]));
    }
  };

  const handlePresetClick = (preset: VideoDurationPreset) => {
    emitChange(preset.min, preset.max);
  };

  const isPresetActive = (preset: VideoDurationPreset) => {
    if (!isActive) {
      return false;
    }
    const selectedOption = section.options.find((opt) => opt.isSelected);
    return selectedOption?.value === `${preset.min}-${preset.max}`;
  };

  return (
    <>
      <CollapsibleSection
        isCollapsed={isInitiallyCollapsed}
        title={FilterSectionTitle[section.paramKey]}
      >
        <PresetChips>
          {VIDEO_DURATION_PRESETS.map((preset) => (
            <PresetChip
              $isActive={isPresetActive(preset)}
              aria-pressed={isPresetActive(preset)}
              key={preset.id}
              onClick={() => handlePresetClick(preset)}
              trackingId={`${MediaLibraryTrackingIds.VIDEO_DURATION_CHIP}-${preset.id}`}
              type='button'
            >
              {preset.label}
            </PresetChip>
          ))}
        </PresetChips>

        <Slider
          maxValue={MAX_VIDEO_DURATION_INDEX}
          minValue={0}
          onChange={handleSliderChange}
          step={1}
          value={[minIndex, maxIndex]}
        >
          <Slider.Track>
            <>
              <Slider.FillTrack
                maxPosition={maxIndex / MAX_VIDEO_DURATION_INDEX}
                minPosition={minIndex / MAX_VIDEO_DURATION_INDEX}
              />
              <Slider.Thumb
                aria-valuetext={formatDurationValue(indexToSeconds(minIndex))}
                ariaLabel='Minimum duration'
                index={0}
                trackingIdPrefix={MEDIA_LIBRARY_FILTER_PANEL_TRACKING_ID_PREFIX}
              />
              <Slider.Thumb
                aria-valuetext={formatDurationValue(indexToSeconds(maxIndex))}
                ariaLabel='Maximum duration'
                index={1}
                trackingIdPrefix={MEDIA_LIBRARY_FILTER_PANEL_TRACKING_ID_PREFIX}
              />
            </>
          </Slider.Track>
        </Slider>

        <TickMarks>
          {VIDEO_DURATION_TICK_LABELS.map((label, index) => (
            <Tick $position={index / MAX_VIDEO_DURATION_INDEX} key={label}>
              {label}
            </Tick>
          ))}
        </TickMarks>
      </CollapsibleSection>
      <HR />
    </>
  );
};

const PresetChips = styled.div(
  ({ theme }) => css`
    display: flex;
    flex-wrap: nowrap;
    gap: ${theme.spacings[1]};
  `,
);

const PresetChip = withTracking(
  styled.button<{ $isActive: boolean }>(
    ({ $isActive, theme }) => css`
      display: inline-flex;
      align-items: center;
      justify-content: center;
      flex: 0 0 auto;
      padding: 2px ${theme.spacings[1]};
      border-radius: ${theme.borderRadii.pill};
      border: 1px solid
        ${$isActive ? theme.colors.aliases.new : theme.colors.gray3};
      background-color: ${theme.colors.white};
      color: ${$isActive
        ? theme.colors.aliases.new
        : theme.colors.aliases.bodyPrimary};
      cursor: pointer;
      font-family: inherit;
      font-size: ${theme.fontSizes[6]};
      line-height: 1.4;
      white-space: nowrap;
      appearance: none;
      outline: none;
      -webkit-tap-highlight-color: transparent;

      &:hover {
        border-color: ${theme.colors.aliases.new};
      }

      &:focus-visible {
        outline: 2px solid ${theme.colors.aliases.focus};
        outline-offset: 2px;
      }
    `,
  ),
);

const TickMarks = styled.div`
  position: relative;
  width: 100%;
  height: 20px;
`;

const Tick = styled.span<{ $position: number }>(
  ({ $position, theme }) => css`
    position: absolute;
    left: ${$position * 100}%;
    transform: translateX(-50%);
    font-size: ${theme.fontSizes[6]};
    color: ${theme.colors.aliases.bodySecondary};
  `,
);
